// 編集画面の画像の並び（最大3枚。先頭が表紙）。画面で見せるための情報（サムネイルなど）と、
// サーバーへ送る並び（FutureImageSelection）を、いっしょに持つ。
import type { FutureImage, FutureImageSelection, FuturePreset } from "../../api/futureBoard";
// images.ts の MAX_IMAGES_PER_CARD と同じ値（node の単体テストで相対 import を避けるため、ここに置く）
const MAX_IMAGES = 3;

export interface EditorImage {
  /** 画面内で一意の鍵（upload:<id> / preset:<key>） */
  key: string;
  selection: FutureImageSelection;
  /** サーバーの画像の行の ID。カードに保存済みの画像と、登録済みの写真にある（新しく選んだプリセットにはまだない） */
  id: number | null;
  thumbUrl: string | null;
  /** 本人が付けた説明、またはプリセットの説明（表示用） */
  alt: string | null;
  focalX: number;
  focalY: number;
  /** この編集で登録した、まだカードに保存していない自分の写真 */
  fresh: boolean;
}

const keyOf = (selection: FutureImageSelection) => (selection.type === "preset" ? `preset:${selection.preset_key}` : `upload:${selection.image_id}`);

// 保存済みのカードの画像（position の順）から。
export function fromCardImages(images: readonly FutureImage[]): EditorImage[] {
  return [...images]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(image => {
      const selection: FutureImageSelection = image.type === "preset" && image.preset_key
        ? { type: "preset", preset_key: image.preset_key }
        : { type: "upload", image_id: image.id };
      return {
        key: keyOf(selection), selection, id: image.id, thumbUrl: image.thumb_url, alt: image.alt_text,
        focalX: image.focal_x, focalY: image.focal_y, fresh: false,
      };
    });
}

export function fromPreset(preset: Pick<FuturePreset, "key" | "alt_text" | "thumb_url">): EditorImage {
  const selection: FutureImageSelection = { type: "preset", preset_key: preset.key };
  return { key: keyOf(selection), selection, id: null, thumbUrl: preset.thumb_url, alt: preset.alt_text, focalX: 50, focalY: 50, fresh: false };
}

// 登録したばかりの自分の写真（カードにはまだ付いていない）。
export function fromUpload(image: Pick<FutureImage, "id" | "thumb_url" | "alt_text" | "focal_x" | "focal_y">): EditorImage {
  const selection: FutureImageSelection = { type: "upload", image_id: image.id };
  return { key: keyOf(selection), selection, id: image.id, thumbUrl: image.thumb_url, alt: image.alt_text, focalX: image.focal_x, focalY: image.focal_y, fresh: true };
}

export const selectionOf = (items: readonly EditorImage[]): FutureImageSelection[] => items.map(item => item.selection);

export const canAdd = (items: readonly EditorImage[]): boolean => items.length < MAX_IMAGES;
export const hasItem = (items: readonly EditorImage[], key: string): boolean => items.some(item => item.key === key);

// 追加。上限（3枚）と重複は、そのまま返す（追加しない）。
export function addItem(items: readonly EditorImage[], item: EditorImage): EditorImage[] {
  if (!canAdd(items) || hasItem(items, item.key)) return [...items];
  return [...items, item];
}

// 取り除く。まだカードに保存していない自分の写真は、サーバーにも残す理由がないので、削除の対象として返す。
export function removeItem(items: readonly EditorImage[], index: number): { items: EditorImage[]; discard: EditorImage | null } {
  const target = items[index];
  if (!target) return { items: [...items], discard: null };
  return { items: items.filter((_, position) => position !== index), discard: target.fresh && target.id !== null ? target : null };
}

export function moveItem(items: readonly EditorImage[], from: number, to: number): EditorImage[] {
  const result = [...items];
  if (from < 0 || from >= result.length || to < 0 || to >= result.length || from === to) return result;
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);
  return result;
}

export const makeCoverItem = (items: readonly EditorImage[], index: number): EditorImage[] => moveItem(items, index, 0);

// 保存した内容と並びが違うか（画像の変更があるか）。
export function sameImages(a: readonly EditorImage[], b: readonly EditorImage[]): boolean {
  return a.length === b.length && a.every((item, index) => item.key === b[index].key);
}

// 画像の設定（位置・説明）を、画面の状態に反映する。
export function withSettings(items: readonly EditorImage[], key: string, settings: { alt?: string | null; focalX?: number; focalY?: number }): EditorImage[] {
  return items.map(item => (item.key === key ? {
    ...item,
    ...(settings.alt !== undefined ? { alt: settings.alt } : {}),
    ...(settings.focalX !== undefined ? { focalX: settings.focalX } : {}),
    ...(settings.focalY !== undefined ? { focalY: settings.focalY } : {}),
  } : item));
}
